/**
 * Path rewriting utilities for repeatable mode.
 * Prefixes all field paths in effects/observers with `items.{index}.`
 * so that per-row reactive logic operates on the correct field paths.
 */

import type { FormEffect, FormObserver } from '../types/block';
import type { Compute } from '../types/compute';
import type { Condition } from '../types/conditions';

export function prefixEffects(effects: FormEffect[], rowPrefix: string): FormEffect[] {
  return effects.map((effect) => ({
    ...effect,
    watch: Array.isArray(effect.watch)
      ? effect.watch.map((w) => prefixPath(w, rowPrefix))
      : prefixPath(effect.watch, rowPrefix),
    clear: effect.clear?.map((c) => prefixPath(c, rowPrefix)),
    set: effect.set
      ? Object.fromEntries(
          Object.entries(effect.set).map(([key, compute]) => [
            prefixPath(key, rowPrefix),
            prefixCompute(compute, rowPrefix),
          ]),
        )
      : undefined,
    condition: effect.condition ? prefixCondition(effect.condition, rowPrefix) : undefined,
  }));
}

export function prefixObservers(observers: FormObserver[], rowPrefix: string): FormObserver[] {
  return observers.map((observer) => ({
    ...observer,
    watch: prefixPath(observer.watch, rowPrefix),
    lookup: {
      ...observer.lookup,
      paramField: observer.lookup.paramField,
      params: observer.lookup.params
        ? Object.fromEntries(
            Object.entries(observer.lookup.params).map(([key, path]) => [
              key,
              prefixPath(path, rowPrefix),
            ]),
          )
        : undefined,
    },
    mapTo: Object.fromEntries(
      Object.entries(observer.mapTo).map(([resultKey, formField]) => [
        resultKey,
        prefixPath(formField, rowPrefix),
      ]),
    ),
    condition: observer.condition ? prefixCondition(observer.condition, rowPrefix) : undefined,
  }));
}

function isComputeNode(value: unknown): value is Compute {
  return (
    typeof value === 'object' &&
    value !== null &&
    'operator' in value &&
    typeof (value as { operator: unknown }).operator === 'string'
  );
}

export function prefixPath(path: string, prefix: string): string {
  if (path.startsWith('^')) return path.slice(1);
  return `${prefix}.${path}`;
}

export function prefixCompute(compute: Compute, prefix: string): Compute {
  switch (compute.operator) {
    case 'field':
      return { ...compute, name: prefixPath(compute.name, prefix) };
    case 'optionProp':
      return { ...compute, field: prefixPath(compute.field, prefix) };
    case 'coalesce':
      return {
        ...compute,
        fields: compute.fields.map((fieldPath) => prefixPath(fieldPath, prefix)),
      };
    case 'switch':
      return {
        ...compute,
        on: compute.on ? prefixPath(compute.on, prefix) : undefined,
      };
    case 'ifElse':
      return {
        ...compute,
        condition: prefixCondition(compute.condition, prefix),
        then: isComputeNode(compute.then)
          ? prefixCompute(compute.then as Compute, prefix)
          : compute.then,
        else: isComputeNode(compute.else)
          ? prefixCompute(compute.else as Compute, prefix)
          : compute.else,
      };
    case 'not':
      return {
        ...compute,
        condition: prefixCondition(compute.condition, prefix),
      };
    case 'concat':
      return {
        ...compute,
        parts: compute.parts.map((part) =>
          typeof part === 'string' ? part : { field: prefixPath(part.field, prefix) },
        ),
      };
    case 'template':
      return {
        ...compute,
        template: compute.template.replace(/\{([^}]+)\}/g, (_, fieldPath: string) =>
          `{${prefixPath(fieldPath.trim(), prefix)}}`,
        ),
      };
    case 'add':
    case 'subtract':
    case 'multiply':
    case 'min':
    case 'max':
      return {
        ...compute,
        operands: compute.operands.map((operand) =>
          typeof operand === 'number' ? operand : prefixPath(operand, prefix),
        ),
      };
    case 'divide':
      return {
        ...compute,
        dividend:
          typeof compute.dividend === 'number'
            ? compute.dividend
            : prefixPath(compute.dividend, prefix),
        divisor:
          typeof compute.divisor === 'number'
            ? compute.divisor
            : prefixPath(compute.divisor, prefix),
      };
    case 'round':
    case 'clamp':
      return {
        ...compute,
        source:
          compute.source === undefined
            ? undefined
            : typeof compute.source === 'number'
              ? compute.source
              : prefixPath(compute.source, prefix),
      };
    case 'percent':
      return {
        ...compute,
        part:
          typeof compute.part === 'number' ? compute.part : prefixPath(compute.part, prefix),
        whole:
          typeof compute.whole === 'number' ? compute.whole : prefixPath(compute.whole, prefix),
      };
    case 'appendUnique':
    case 'removeFromArray':
      return { ...compute, target: prefixPath(compute.target, prefix) };
    case 'arrayLength':
    case 'arrayMap':
    case 'arrayFilter':
      return { ...compute, source: prefixPath(compute.source, prefix) };
    case 'dateOffset':
      return {
        ...compute,
        source: compute.source
          ? (typeof compute.source === 'object' && 'operator' in compute.source
              ? prefixCompute(compute.source as Compute, prefix)
              : prefixPath(compute.source as string, prefix))
          : undefined,
        days: typeof compute.days === 'string' ? prefixPath(compute.days, prefix) : compute.days,
        months: typeof compute.months === 'string' ? prefixPath(compute.months, prefix) : compute.months,
        years: typeof compute.years === 'string' ? prefixPath(compute.years, prefix) : compute.years,
      };
    case 'dateDiff':
      return {
        ...compute,
        from: prefixPath(compute.from, prefix),
        to: prefixPath(compute.to, prefix),
      };
    case 'formatDate':
      return {
        ...compute,
        source: compute.source ? prefixPath(compute.source, prefix) : undefined,
      };
    case 'pipe':
      return {
        ...compute,
        steps: compute.steps.map((step) => prefixCompute(step, prefix)),
      };
    default:
      return compute;
  }
}

export function prefixCondition(condition: Condition, prefix: string): Condition {
  if (typeof condition !== 'object' || condition === null) {
    return condition;
  }

  const c = condition as unknown as Record<string, unknown>;

  if ('field' in c) {
    return { ...condition, field: prefixPath(c.field as string, prefix) } as Condition;
  }
  if ('optionProp' in c) {
    return { ...condition, optionProp: prefixPath(c.optionProp as string, prefix) } as Condition;
  }
  if ('allOf' in c) {
    return { ...condition, allOf: (c.allOf as Condition[]).map((child) => prefixCondition(child, prefix)) } as Condition;
  }
  if ('anyOf' in c) {
    return { ...condition, anyOf: (c.anyOf as Condition[]).map((child) => prefixCondition(child, prefix)) } as Condition;
  }
  if ('not' in c) {
    return { ...condition, not: prefixCondition(c.not as Condition, prefix) } as Condition;
  }

  return condition;
}
